/**
 * Verbindlichkeiten-Tab der Buchhaltungsseite
 * Inkl. Gläubiger-Karten, Timeline, Zahlungen, Mahnungen
 */

import { useState } from 'react';
import {
  Plus, X, AlertTriangle, Building2,
  Scale, Clock, CircleDollarSign, Loader2,
  History, ChevronRight, FileText, Users, Percent,
  CalendarCheck, Gavel, Calendar, MessageSquare, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export function VerbindlichkeitenTab({ verbindlichkeiten, glaeubiger, selectedKlient, loadData, openChildTab }) {
  const summeVerbindlichkeiten = verbindlichkeiten.reduce((s, v) => s + (v.aktueller_stand || 0), 0);

  // Timeline State
  const [selectedVerbindlichkeit, setSelectedVerbindlichkeit] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // ==================== HANDLERS ====================

  const handleZahlungErfassen = async (verbindlichkeitId, betrag) => {
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/verbindlichkeiten/${verbindlichkeitId}/zahlung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ betrag, datum: new Date().toISOString().split('T')[0] })
      }).then(r => r.json());
      if (result.success) {
        toast.success(`Zahlung erfasst. Neuer Stand: ${result.neuer_stand.toFixed(2)} €`);
        loadData();
        if (selectedVerbindlichkeit === verbindlichkeitId) loadTimeline(verbindlichkeitId);
      }
    } catch { toast.error('Fehler beim Erfassen der Zahlung'); }
  };

  const loadTimeline = async (verbindlichkeitId) => {
    setTimelineLoading(true);
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/verbindlichkeiten/${verbindlichkeitId}/timeline?include_future=true`).then(r => r.json());
      setTimeline(result);
      setSelectedVerbindlichkeit(verbindlichkeitId);
    } catch { toast.error('Timeline konnte nicht geladen werden'); }
    finally { setTimelineLoading(false); }
  };

  const handleMahnungErfassen = async (verbindlichkeitId) => {
    const forderung = prompt('Forderungshöhe in der Mahnung (optional):', '');
    const mahnstufe = prompt('Mahnstufe (1, 2, 3, ...):', '1');
    try {
      let url = `${BACKEND_URL}/api/buchhaltung/verbindlichkeiten/${verbindlichkeitId}/mahnung?`;
      if (forderung) url += `forderungshoehe=${forderung}&`;
      if (mahnstufe) url += `mahnstufe=${mahnstufe}`;
      const result = await fetch(url, { method: 'POST' }).then(r => r.json());
      if (result.success) {
        toast.success('Mahnung erfasst');
        loadData();
        if (selectedVerbindlichkeit === verbindlichkeitId) loadTimeline(verbindlichkeitId);
      }
    } catch { toast.error('Fehler beim Erfassen der Mahnung'); }
  };

  // ==================== TIMELINE ICON/COLOR MAPS ====================

  const iconMap = {
    entstehung: <FileText className="w-4 h-4" />,
    zahlung: <CircleDollarSign className="w-4 h-4" />,
    mahnung: <AlertTriangle className="w-4 h-4" />,
    glaeubiger_wechsel: <Users className="w-4 h-4" />,
    zins_aenderung: <Percent className="w-4 h-4" />,
    ratenzahlung_start: <CalendarCheck className="w-4 h-4" />,
    vollstreckung: <Gavel className="w-4 h-4" />,
    faelligkeit: <Clock className="w-4 h-4" />,
    geplante_zahlung: <Calendar className="w-4 h-4" />,
    notiz: <MessageSquare className="w-4 h-4" />
  };

  const farbMap = {
    green: 'bg-green-100 text-green-600 border-green-200',
    amber: 'bg-amber-100 text-amber-600 border-amber-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    violet: 'bg-violet-100 text-violet-600 border-violet-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-4" data-testid="verbindlichkeiten-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <p className="text-sm text-slate-500">{verbindlichkeiten.length} Verbindlichkeiten</p>
          <p className="text-lg font-bold text-amber-700">
            Gesamt: {summeVerbindlichkeiten.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => openChildTab('/schnellerfassung/verbindlichkeit', { title: 'Neue Verbindlichkeit', forceNew: true, params: { klientId: selectedKlient, glaeubiger } })} data-testid="btn-verbindlichkeit-anlegen">
            <Plus className="w-4 h-4 mr-2" /> Verbindlichkeit anlegen
          </Button>
          <Button variant="outline" size="sm" onClick={() => openChildTab('/schnellerfassung/glaeubiger', { title: 'Neuer Gläubiger', forceNew: true, params: { klientId: selectedKlient } })}>
            <Users className="w-4 h-4 mr-2" /> Gläubiger anlegen
          </Button>
        </div>
      </div>

      {/* Gläubiger-Übersicht */}
      {glaeubiger.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {glaeubiger.map(g => {
            const schulden = verbindlichkeiten.filter(v => v.glaeubiger_id === g.id).reduce((s, v) => s + (v.aktueller_stand || 0), 0);
            return (
              <Card key={g.id} className="border-l-4 border-l-slate-400">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700 text-sm">{g.name}</span>
                  </div>
                  {schulden > 0 && <p className="text-amber-700 font-bold mt-1">{schulden.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>}
                  <Badge variant="outline" className="text-xs mt-1">{g.typ || 'Sonstig'}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Verbindlichkeiten-Liste */}
      {verbindlichkeiten.length > 0 ? (
        <div className="space-y-3">
          {verbindlichkeiten.map(v => {
            const glaeubigerObj = glaeubiger.find(g => g.id === v.glaeubiger_id);
            const tilgung = ((v.ursprungsbetrag - v.aktueller_stand) / v.ursprungsbetrag * 100) || 0;
            const istFaellig = v.faelligkeitsdatum && new Date(v.faelligkeitsdatum) <= new Date();
            
            return (
              <Card key={v.id} className={istFaellig ? 'border-l-4 border-l-red-500' : ''}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-800">{v.bezeichnung}</p>
                        {istFaellig && <Badge variant="destructive" className="text-xs"><Clock className="w-3 h-3 mr-1" /> Fällig</Badge>}
                      </div>
                      <p className="text-sm text-slate-500">
                        {glaeubigerObj?.name || 'Unbekannter Gläubiger'}
                        {v.aktenzeichen && ` - AZ: ${v.aktenzeichen}`}
                      </p>
                      {v.ratenzahlung && (
                        <p className="text-xs text-slate-400 mt-1">
                          Ratenzahlung: {v.rate_betrag?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € / {v.rate_intervall || 'monatlich'}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-amber-700">{(v.aktueller_stand || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>
                      <p className="text-xs text-slate-400">von {(v.ursprungsbetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} € ({tilgung.toFixed(0)}% getilgt)</p>
                      {v.faelligkeitsdatum && <p className="text-xs text-slate-500 mt-1">Fällig: {new Date(v.faelligkeitsdatum).toLocaleDateString('de-DE')}</p>}
                    </div>
                  </div>
                  
                  {/* Progress */}
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 transition-all" style={{ width: `${tilgung}%` }} />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Button variant="outline" size="sm" onClick={() => { const betrag = prompt('Zahlungsbetrag eingeben:', v.rate_betrag || ''); if (betrag) handleZahlungErfassen(v.id, parseFloat(betrag)); }}>
                      <CircleDollarSign className="w-4 h-4 mr-1" /> Zahlung erfassen
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleMahnungErfassen(v.id)}>
                      <AlertTriangle className="w-4 h-4 mr-1" /> Mahnung
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => loadTimeline(v.id)} className="text-violet-600 hover:text-violet-700">
                      <History className="w-4 h-4 mr-1" /> Timeline
                    </Button>
                  </div>
                  
                  {/* Timeline */}
                  {selectedVerbindlichkeit === v.id && timeline && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-slate-700 flex items-center gap-2"><History className="w-4 h-4" /> Zahlungsverlauf</h4>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedVerbindlichkeit(null)}><X className="w-4 h-4" /></Button>
                      </div>
                      {timelineLoading ? (
                        <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
                      ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {timeline.timeline?.map((event, idx) => {
                            const isGeplant = event.geplant;
                            const farbe = farbMap[event.farbe] || farbMap.slate;
                            return (
                              <div key={event.id || idx} className={`flex items-start gap-3 p-2 rounded-lg border ${farbe} ${isGeplant ? 'opacity-60 border-dashed' : ''}`}>
                                <div className="shrink-0 mt-0.5">{iconMap[event.typ] || <ChevronRight className="w-4 h-4" />}</div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium">{event.beschreibung}</p>
                                  <p className="text-xs opacity-70">
                                    {event.datum}
                                    {event.betrag && ` - ${event.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`}
                                    {isGeplant && ' (geplant)'}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {timeline.statistik && (
                        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-center text-xs">
                          <div><p className="text-slate-500">Gezahlt</p><p className="font-bold text-green-600">{timeline.statistik.summe_gezahlt?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p></div>
                          <div><p className="text-slate-500">Mahnungen</p><p className="font-bold text-amber-600">{timeline.statistik.mahnungen_count}</p></div>
                          <div><p className="text-slate-500">Geplant</p><p className="font-bold text-violet-600">{timeline.statistik.geplante_zahlungen}</p></div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <Scale className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Keine Verbindlichkeiten</p>
            <p className="text-sm text-green-600 mt-1">Keine offenen Schulden vorhanden</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
